/**
 * // Class Obj: サイト内の画像やiframeの遅延読み込み
 */
export class GraphicsLazyController
{
  private static readonly LOADED_CLASS_NAME: string = 'is_loaded';
  private readonly mSiteBody: HTMLElement;
  private readonly mLazyTargets: Array<LazyTarget>;
  private readonly mDefaultOptions: Options;

  /**
   * // Constructor: サイト内の背景画像やiframeを監視の対象とするオプションを受け取る
   *
   * @param lazy_targets 遅延読み込みの対象セレクタとオプションの配列
   */
  constructor(lazy_targets: Array<LazyTarget>)
  {
    this.mSiteBody = document.body;
    this.mLazyTargets = lazy_targets; // 遅延読み込み対象を保存
    // 遅延読み込みオプション
    this.mDefaultOptions = {
      root: null, // ビューポートを基準にする
      rootMargin: '0px', // 〇〇px手前で読み込み開始
      threshold: 0, // 0に設定して、任意の交差量でトリガーされるようにする
    };
  }

  /**
   * // Method: 遅延読み込み対象の要素を監視
   */
  public loadGraphics(): void
  {
    // 遅延読み込み対象から、それぞれのプロパティを抽出
    this.mLazyTargets.forEach(({ selector, options }: LazyTarget) =>
    {
      // クラス名を抽出して、initObserverメソッドに渡す
      const class_name: string = selector.replace('.', '');
      this.initObserver(class_name, options);
    });
  }

  /**
   * // Method: IntersectionObserverを初期化
   *
   * @param class_name 監視対象のCSSクラス名
   * @param custom_options IntersectionObserverのカスタムオプション設定
   */
  private initObserver(class_name: string, custom_options = {}): void
  {
    // デフォルトオプションとカスタムオプションを統合（マージ）する
    const options = { ...this.mDefaultOptions, ...custom_options };

    // オブサーバーのコールバック関数をバインドして設定
    const iInsectObserver = new IntersectionObserver(
      this.onIntersection.bind(this),
      options,
    );

    // 監視対象の要素を as 構文で型を変換して取得
    const elements: HTMLCollectionOf<HTMLElement> = this.mSiteBody.getElementsByClassName(class_name) as HTMLCollectionOf<HTMLElement>;

    // 監視対象の要素を一つずつオブサーバーに登録
    for (const element of elements)
    {
      iInsectObserver.observe(element);
    }
  }

  /**
   * // Method: 指定されたエントリーが交差したときに特定の処理を行う
   *
   * @param entries 交差を監視する要素の配列（observer固有のタイプ）
   * @param observer 交差を監視するためのIntersectionObserverインスタンス
   */
  private onIntersection(entries: IntersectionObserverEntry[], observer: IntersectionObserver): void
  {
    // 各エントリーについて処理を行う
    entries.forEach((entry: IntersectionObserverEntry) =>
    {
      // エントリーが交差状態になった場合
      if (entry.isIntersecting)
      {
        const lazy_graphic: Element = entry.target; // 遅延読み込み対象の要素を取得

        // iframe要素の場合、src属性をdata-src属性の値に置き換える
        if (lazy_graphic.nodeName === 'IFRAME')
        {
          // Element型をHTMLIFrameElement型に変換(Element型は.srcプロパティが存在しないため)
          const lazy_iframe: HTMLIFrameElement = lazy_graphic as HTMLIFrameElement;
          // もしdata-src属性が存在すればその値を取得し、存在しなければ空文字列（''）を返す
          lazy_iframe.src = lazy_iframe.getAttribute('data-src') || '';
        }
        //読み込み完了クラスを追加
        lazy_graphic.classList.add(GraphicsLazyController.LOADED_CLASS_NAME);
        // オブサーバーから解除する
        observer.unobserve(lazy_graphic);
      }
    });
  }
}

//////////////////////////////////////////////////
/// AREA: types
//////////////////////////////////////////////////
type Options = {
  root?: null;
  rootMargin?: string;
  threshold?: number;
};
type LazyTarget = {
  selector: string;
  options: Options;
};

//////////////////////////////////////////////////
///  AREA: END
//////////////////////////////////////////////////